<template>
	<div class="relative min-h-[100dvh] w-full bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
		<div class="w-full min-h-[100dvh] flex bg-white dark:bg-gray-800">
			<!-- Left Branding / Logo Section -->
			<div
				class="hidden md:flex w-1/2 bg-gradient-to-tr from-primary to-secondary dark:from-primary-light/10 dark:to-secondary-light/10 items-center justify-center p-8"
			>
				<div class="text-center text-white space-y-6">
					<img  src= 'images/main-logo-dark.png' alt="App Logo" class="mx-auto h-24 w-[18rem] object-contain drop-shadow-md" />
					<h2 class="text-3xl font-bold">{{ appTitle }}</h2>
					<div class="h-8 flex items-center justify-center overflow-hidden">
						<Transition name="rotate" mode="out-in">
							<span class="block text-lg font-medium" :key="currentTagline">{{ taglines[currentTagline] }}</span>
						</Transition>
					</div>
				</div>
			</div>

			<!-- Right Form Section -->
			<div class="flex flex-col justify-start md:justify-center items-center w-full md:w-1/2 p-5 sm:p-8 lg:p-12 pt-16 sm:pt-20 md:pt-10 relative overflow-y-auto min-h-[100dvh] md:min-h-0">
				<!-- Dark/Light Mode Toggle -->
				<button
				@click="themeStore.toggleTheme()"
				class="absolute top-4 right-6 p-2 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition"
				aria-label="Toggle dark mode"
				>
					<component v-if="isDarkMode" :is="Sun" />
					<component v-else :is="Moon" />
				</button>

				<div class="mb-4 md:hidden flex justify-center pb-4">
					<img
						:src="isDarkMode ? 'images/main-logo-dark.png' : 'images/main-logo.png'"
						alt="ProWeaver logo"
						class="h-14 sm:h-16 w-auto object-contain drop-shadow-sm"
					/>
				</div>

				<!-- Form Container with Border -->
				<div class="w-full max-w-[50rem] space-y-4 md:space-y-8">
					<div class="bg-white dark:bg-gray-800 p-6 md:p-8 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700">
						<div class="mb-6">
							<h1 class="text-2xl text-center sm:text-3xl font-bold text-gray-900 dark:text-white">Create an account</h1>
						</div>

						<!-- Success Message -->
						<div
						v-if="success"
						class="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg"
						>
							<p class="text-green-800 dark:text-green-200 font-medium">Account created successfully! Redirecting to login...</p>
						</div>

						<!-- Registration Form -->
						<form class="space-y-4" @submit.prevent="handleSubmit">
							<!-- First Name & Last Name (Two Column) -->
							<div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
								<!-- First Name -->
								<div>
									<label for="firstName" class="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
										First Name <span class="text-red-500">*</span>
									</label>
									<input
									id="firstName"
									v-model="firstName"
									type="text"
									placeholder="John"
									required
									class="block w-full rounded-md border border-gray-300 p-3 bg-gray-50 text-gray-900 sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:focus:ring-primary-dark dark:focus:border-primary-dark"
									/>
									<p v-if="validationErrors.firstName" class="mt-1 text-sm text-red-500 dark:text-red-400">
										{{ validationErrors.firstName }}
									</p>
								</div>

								<!-- Last Name -->
								<div>
									<label for="lastName" class="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
										Last Name <span class="text-red-500">*</span>
									</label>
									<input
										id="lastName"
										v-model="lastName"
										type="text"
										placeholder="Doe"
										required
										class="block w-full rounded-md border border-gray-300 p-3 bg-gray-50 text-gray-900 sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:focus:ring-primary-dark dark:focus:border-primary-dark"
									/>
									<p v-if="validationErrors.lastName" class="mt-1 text-sm text-red-500 dark:text-red-400">
										{{ validationErrors.lastName }}
									</p>
								</div>
							</div>

							<!-- Email & Username (Two Column) -->
							<div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
								<!-- Email -->
								<div>
									<label for="email" class="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
										Email <span class="text-red-500">*</span>
									</label>
									<input
										id="email"
										v-model="email"
										type="email"
										placeholder="john@example.com"
										required
										class="block w-full rounded-md border border-gray-300 p-3 bg-gray-50 text-gray-900 sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:focus:ring-primary-dark dark:focus:border-primary-dark"
									/>
									<p v-if="validationErrors.email" class="mt-1 text-sm text-red-500 dark:text-red-400">
										{{ validationErrors.email }}
									</p>
								</div>

								<!-- Username -->
								<div>
									<label for="username" class="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
										Username <span class="text-red-500">*</span>
									</label>
									<input
										id="username"
										v-model="username"
										@input="username = $event.target.value.replace(/\s+/g, '')"
										type="text"
										placeholder="johndoe"
										required
										class="block w-full rounded-md border border-gray-300 p-3 bg-gray-50 text-gray-900 sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:focus:ring-primary-dark dark:focus:border-primary-dark"
									/>
									<p v-if="validationErrors.username" class="mt-1 text-sm text-red-500 dark:text-red-400">
										{{ validationErrors.username }}
									</p>
								</div>
							</div>

							<!-- Phone & Date of Birth (Two Column) -->
							<div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
								<!-- Phone -->
								<div>
									<label for="phone" class="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
										Phone Number
									</label>
									<input
										id="phone"
										v-model="phone"
										type="tel"
										placeholder="+1 (555) 123-4567"
										class="block w-full rounded-md border border-gray-300 p-3 bg-gray-50 text-gray-900 sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:focus:ring-primary-dark dark:focus:border-primary-dark"
									/>
									<p v-if="validationErrors.phone" class="mt-1 text-sm text-red-500 dark:text-red-400">
										{{ validationErrors.phone }}
									</p>
								</div>

								<!-- Date of Birth -->
								<div>
									<label for="dateOfBirth" class="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
										Date of Birth
									</label>
									<input
										id="dateOfBirth"
										v-model="dateOfBirth"
										type="date"
										placeholder="mm/dd/yyyy"
										class="date-input block w-full rounded-md border border-gray-300 p-3 bg-gray-50 sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary dark:bg-gray-700 dark:border-gray-600 dark:focus:ring-primary-dark dark:focus:border-primary-dark"
										:class="dateOfBirth ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-400 date-empty'"
									/>
									<p v-if="validationErrors.dateOfBirth" class="mt-1 text-sm text-red-500 dark:text-red-400">
										{{ validationErrors.dateOfBirth }}
									</p>
								</div>
							</div>

							<!-- Gender && Position -->
							<div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
								<!-- Gender -->
								<div>
									<label for="gender" class="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
									Gender
									</label>
									<select
									id="gender"
									v-model="gender"
									class="block w-full rounded-md border border-gray-300 p-3 bg-gray-50 sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary dark:bg-gray-700 dark:border-gray-600 dark:focus:ring-primary-dark dark:focus:border-primary-dark"
									:class="gender ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-400'"
									>
										<option value="">Select Gender</option>
										<option value="male">Male</option>
										<option value="female">Female</option>
										<option value="other">Other</option>
									</select>
									<p v-if="validationErrors.gender" class="mt-1 text-sm text-red-500 dark:text-red-400">
										{{ validationErrors.gender }}
									</p>
								</div>

								<!-- Position -->
								<div>
									<label for="position" class="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
										Position
									</label>
									<select
									id="position"
									v-model="position"
									required
									class="block w-full rounded-md border border-gray-300 p-3 bg-gray-50 sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary dark:bg-gray-700 dark:border-gray-600 dark:focus:ring-primary-dark dark:focus:border-primary-dark"
									:class="position ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-400'"
									>
										<option value="" Selected>Select Position</option>
										<option value="1">HHA</option>
										<option value="2">CNA</option>
										<option value="3">PCA</option>
										<option value="4">DSP</option>
										<option value="5">RN</option>
										<option value="6">LPN</option>
									</select>
									<p v-if="validationErrors.position" class="mt-1 text-sm text-red-500 dark:text-red-400">
										{{ validationErrors.position }}
									</p>
								</div>
							</div>

							<!-- Street (Full Width) -->
							<div>
								<label for="street" class="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
								Street Address
								</label>
								<input
								id="street"
								v-model="street"
								type="text"
								placeholder="123 Main Street"
								class="block w-full rounded-md border border-gray-300 p-3 bg-gray-50 text-gray-900 sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:focus:ring-primary-dark dark:focus:border-primary-dark"
								/>
								<p v-if="validationErrors.street" class="mt-1 text-sm text-red-500 dark:text-red-400">
								{{ validationErrors.street }}
								</p>
							</div>

							<!-- City, State & Zipcode (Three Column) -->
							<div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
								<!-- City -->
								<div>
								<label for="city" class="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
									City
								</label>
								<input
									id="city"
									v-model="city"
									type="text"
									placeholder="New York"
									class="block w-full rounded-md border border-gray-300 p-3 bg-gray-50 text-gray-900 sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:focus:ring-primary-dark dark:focus:border-primary-dark"
								/>
								<p v-if="validationErrors.city" class="mt-1 text-sm text-red-500 dark:text-red-400">
									{{ validationErrors.city }}
								</p>
								</div>

								<!-- State -->
								<div>
									<label for="state" class="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
										State
									</label>
									<select
										id="state"
										v-model="state"
										class="block w-full rounded-md border border-gray-300 p-3 bg-gray-50 sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary dark:bg-gray-700 dark:border-gray-600 dark:focus:ring-primary-dark dark:focus:border-primary-dark"
										:class="state ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-400'"
									>
										<option value="">Select a state</option>
										<option
											v-for="s in usStates"
											:key="s"
											:value="s"
										>
											{{ s }}
										</option>
									</select>
									<p v-if="validationErrors.state" class="mt-1 text-sm text-red-500 dark:text-red-400">
										{{ validationErrors.state }}
									</p>
								</div>

								<!-- Zipcode -->
								<div>
									<label for="zipcode" class="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
										Zipcode
									</label>
									<input
										id="zipcode"
										v-model="zipcode"
										type="text"
										placeholder="10001"
										class="block w-full rounded-md border border-gray-300 p-3 bg-gray-50 text-gray-900 sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:focus:ring-primary-dark dark:focus:border-primary-dark"
									/>
									<p v-if="validationErrors.zipcode" class="mt-1 text-sm text-red-500 dark:text-red-400">
										{{ validationErrors.zipcode }}
									</p>
								</div>
							</div>

							<!-- Password & Confirm Password (Two Column) -->
							<div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
								<!-- Password -->
								<div>
									<label for="password" class="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
										Password <span class="text-red-500">*</span>
									</label>
									<div class="relative">
										<input
											id="password"
											v-model="password"
											:type="showPassword ? 'text' : 'password'"
											placeholder="Enter password"
											required
											minlength="8"
											class="block w-full rounded-md border border-gray-300 p-3 bg-gray-50 text-gray-900 sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:focus:ring-primary-dark dark:focus:border-primary-dark pr-10"
										/>
										<button
											type="button"
											@click="showPassword = !showPassword"
											class="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
										>
											<component :is="showPassword ? EyeOff : Eye" class="h-5 w-5" />
										</button>
									</div>
									<p v-if="validationErrors.password" class="mt-1 text-sm text-red-500 dark:text-red-400">
										{{ validationErrors.password }}
									</p>
								</div>

								<!-- Confirm Password -->
								<div>
									<label for="confirmPassword" class="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
										Confirm Password <span class="text-red-500">*</span>
									</label>
									<div class="relative">
										<input
											id="confirmPassword"
											v-model="confirmPassword"
											:type="showConfirmPassword ? 'text' : 'password'"
											placeholder="Confirm password"
											required
											minlength="8"
											class="block w-full rounded-md border border-gray-300 p-3 bg-gray-50 text-gray-900 sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:focus:ring-primary-dark dark:focus:border-primary-dark pr-10"
										/>
										<button
											type="button"
											@click="showConfirmPassword = !showConfirmPassword"
											class="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
										>
											<component :is="showConfirmPassword ? EyeOff : Eye" class="h-5 w-5" />
										</button>
									</div>
									<p v-if="validationErrors.confirmPassword" class="mt-1 text-sm text-red-500 dark:text-red-400">
										{{ validationErrors.confirmPassword }}
									</p>
								</div>
							</div>

							<!-- Submit Button (DISABLED) -->
							<button
								type="submit"
								:disabled="loading"
								class="w-full flex justify-center py-3 bg-primary text-white font-semibold rounded-lg shadow-md transition hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed"
							>
								<LoaderCircle v-if="loading" class="w-5 h-5 mr-2 animate-spin" />
								{{ loading ? 'Creating Account...' : 'Create Account' }}
							</button>
						</form>

						<!-- Login Link -->
						<p class="mt-6 text-sm text-center text-gray-600 dark:text-gray-400">
						Already have an account?
						<a
							href="/portal/login"
							class="text-primary hover:text-primary-hover dark:text-secondary-dark-text dark:hover:text-secondary-dark-hover font-bold hover:underline"
						>Login</a>
						</p>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useRegistration } from '@/composables/auth/useRegistration'
import { useThemeStore } from '@/stores/theme'
import { Sun, Moon, Eye, EyeOff, LoaderCircle } from 'lucide-vue-next'
import { useToast } from 'vue-toastification'

const toast = useToast()
const toastTimeout = 5000

const appTitle = computed(() => process.env.VUE_APP_TITLE || 'PLANET Home Healthcare')

// Router
const router = useRouter()

const taglines = [
	'Create your account to get started.',
	'Join Our Community',
	'Begin Your Journey',
	'Create Your Future',
	'Start Making a Difference',
]
const currentTagline = ref(0)

// Theme Store
const themeStore = useThemeStore()
const isDarkMode = computed(() => themeStore.isDarkMode)

// Form Fields - Individual Refs (All employee fields)
const firstName = ref('')
const lastName = ref('')
const email = ref('')
const username = ref('')
const phone = ref('')
const dateOfBirth = ref('')
const gender = ref('')
const position = ref('')
const street = ref('')
const city = ref('')
const state = ref('')
const zipcode = ref('')
const password = ref('')
const confirmPassword = ref('')
const termsAgreed = ref(true) // Auto-agree to terms (checkbox removed from UI)
const showPassword = ref(false)
const showConfirmPassword = ref(false)

const usStates = [
	'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California',
	'Colorado', 'Connecticut', 'Delaware', 'Florida', 'Georgia',
	'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
	'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland',
	'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 'Missouri',
	'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey',
	'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
	'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina',
	'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
	'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming',
]

// Registration Composable - Pass refs as parameter
const {
	loading,
	error,
	success,
	validationErrors,
	handleRegister
} = useRegistration({
	firstName,
	lastName,
	username,
	email,
	phone,
	dateOfBirth,
	gender,
	position,
	street,
	city,
	state,
	zipcode,
	password,
	confirmPassword,
	termsAgreed
})

// Form Submission
const handleSubmit = async () => {
	error.value = ''
	success.value = false

	try {
		const isSuccess = await handleRegister()

		if (isSuccess) {
			toast.success('Registration completed successfully!', {timeout: 2000})
			setTimeout(() => {
				router.push('/login')
			}, 2000)
		} else {
			toast.error(error.value, { timeout: toastTimeout })
		}
	} catch (err: any) {
		error.value = err?.message || 'An error occurred during registration. Please try again.'
	}
}

// Cleanup on mount
onMounted(() => {
	success.value = false
	error.value = ''
})

onMounted(() => {
	setInterval(() => {
		currentTagline.value = (currentTagline.value + 1) % taglines.length
	}, 3500)
})
</script>

<style scoped>
.date-input.date-empty::-webkit-datetime-edit {
  	color: #9ca3af;
}

.dark .date-input.date-empty::-webkit-datetime-edit {
  	color: #9ca3af;
}

/* Test Rotate Styles */
.rotate-enter-active {
	animation: rotateIn 0.4s ease-out;
}
.rotate-leave-active {
	animation: rotateOut 0.3s ease-in;
}
@keyframes rotateIn {
	from { opacity: 0; transform: translateY(12px) rotateX(-10deg); }
	to { opacity: 1; transform: translateY(0) rotateX(0); }
}
@keyframes rotateOut {
	from { opacity: 1; transform: translateY(0) rotateX(0); }
	to { opacity: 0; transform: translateY(-12px) rotateX(10deg); }
}
</style>


